import { z } from "zod";

import {
  CreateCustomFieldRequest,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";

import { descriptionSchema, displayNameSchema } from "./customFieldFormShared";
import { customFieldEntityTypePlural } from "./customFieldPresentation";

/**
 * Mirrors the SDK's CUSTOM_FIELD_NAME_PATTERN
 * (packages/roboto/src/roboto/domain/custom_fields/record.py): a lowercase
 * ASCII letter followed by up to 62 lowercase letters, digits, or underscores.
 */
export const FIELD_NAME_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

/**
 * Canonical Roboto identity attribute per supported entity type.
 * Mirrors the backend `entity_pk_column` (packages/roboto_internal/.../domain/custom_fields/org_tables.py).
 */
const ENTITY_ID_FIELD_NAMES: Record<TargetEntityType, string> = {
  [TargetEntityType.Collection]: "collection_id",
  [TargetEntityType.Dataset]: "dataset_id",
  [TargetEntityType.Device]: "device_id",
  [TargetEntityType.Event]: "event_id",
  [TargetEntityType.Session]: "session_id",
};

/**
 * Longest an enum option may be, counted on what the user typed once surrounding whitespace
 * is removed. Mirrors the backend's `_MAX_ENUM_VALUE_LENGTH`
 * (packages/roboto/src/roboto/domain/custom_fields/operations.py).
 */
export const MAX_ENUM_VALUE_LENGTH = 256;

/**
 * Mirrors the backend's own cap on how many values an enum field may declare
 * (`_MAX_ENUM_VALUES`, packages/roboto/src/roboto/domain/custom_fields/operations.py).
 * Enforced here so hitting it reads as an inline error rather than a rejected request.
 */
export const MAX_ENUM_OPTIONS = 250;

/**
 * Characters the backend refuses in an enum option, because a value carrying one cannot
 * survive a round trip through RoboQL: a line break has no representation inside a string
 * literal, and a double quote or backslash breaks the query editor's completion insert, which
 * wraps a value in quotes without escaping what it wraps. The control class (Unicode Cc) is
 * refused whole rather than only the members that break. A tab, which breaks json.loads(), is included.
 * Everything else is allowed, including any script's letters, punctuation, and emoji.
 */
const CONTROL_CHARACTER = /\p{Cc}/u;

/**
 * Plain-English names for the control characters people actually end up with, which in
 * practice means pasting from a spreadsheet or a text file. The rest of the class is named by
 * code point instead: no help to most readers, but the alternative is describing an invisible
 * character as "a control character" and leaving them to find it.
 */
const CONTROL_CHARACTER_NAMES: Record<number, string> = {
  0x09: "a tab",
  0x0a: "a line break",
  0x0d: "a line break",
};

/**
 * Names the first character in `value` that cannot be stored, phrased to complete
 * "cannot contain ...", or `undefined` if every character is allowed.
 */
export function forbiddenCharacterIn(value: string): string | undefined {
  for (const character of value) {
    if (character === '"') {
      return "a double quote";
    }
    if (character === "\\") {
      return "a backslash";
    }
    if (CONTROL_CHARACTER.test(character)) {
      const codePoint = character.codePointAt(0) ?? 0;
      return (
        CONTROL_CHARACTER_NAMES[codePoint] ??
        `the character U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`
      );
    }
  }
  return undefined;
}

/**
 * Tidies the whitespace in an enum option: runs become a single space, and surrounding
 * whitespace is removed. This is the visible half of what the server stores, and the only half
 * the form applies. It can only ever shorten a value, so the option the form submits is never
 * longer than the one the user typed and can never fail a limit the form just accepted.
 *
 * JavaScript counts U+FEFF as whitespace where Python does not, so the backend names it
 * alongside `\s` to reach the same result; `\s` alone is correct here. The sets diverge the
 * other way too, on U+001C-001F and U+0085, but those are control characters and a value
 * carrying one is rejected before it is ever tidied.
 */
export function collapseEnumValueWhitespace(value: string): string {
  return value.replace(/\s+/gu, " ").trim();
}

/**
 * Canonical spelling of an enum option, mirroring the backend's `_normalize_enum_value`
 * (packages/roboto/src/roboto/domain/custom_fields/record.py).
 *
 * Used only to decide whether two options would collide once stored. NFC composition is left
 * to the server and deliberately not applied to what the form shows or sends: it renders
 * identically either way, so the user cannot see it, and it can lengthen a value past a limit
 * they were within.
 */
export function normalizeEnumValue(value: string): string {
  return collapseEnumValueWhitespace(value.normalize("NFC"));
}

/** Field types offered by the create form, in the designer's grid order. */
export const CREATE_FIELD_TYPE_OPTIONS: CustomFieldType[] = [
  CustomFieldType.String,
  CustomFieldType.Number,
  CustomFieldType.Enum,
  CustomFieldType.Boolean,
  CustomFieldType.Timestamp,
];

export const DEFAULT_CREATE_CUSTOM_FIELD_VALUES: CreateCustomFieldFormValues = {
  display_name: "",
  field_name: "",
  description: "",
  field_type: CustomFieldType.String,
  enum_values: [{ value: "" }],
};

const createCustomFieldFormObjectSchema = z.object({
  display_name: displayNameSchema,
  field_name: z
    .string()
    .min(1, "Field name is required.")
    .regex(
      FIELD_NAME_REGEX,
      "Start with a lowercase letter; use only lowercase letters, digits, and underscores (max 63).",
    ),
  description: descriptionSchema,
  field_type: z.enum(CustomFieldType),
  // One entry per editor row; useFieldArray needs an object per item. Empty
  // rows are ignored on submit.
  enum_values: z.array(z.object({ value: z.string() })),
});

/** Inferred from the schema so the form's value type can't drift from validation. */
export type CreateCustomFieldFormValues = z.infer<
  typeof createCustomFieldFormObjectSchema
>;

/**
 * Builds the create-form validation schema for `entityType`. The schema is
 * entity-type-aware only so it can reject a `field_name` that shadows the entity's
 * reserved ID column (see `ENTITY_ID_FIELD_NAMES`); every other rule is entity-agnostic.
 */
export function makeCreateCustomFieldFormSchema(entityType: TargetEntityType) {
  return createCustomFieldFormObjectSchema.superRefine((values, ctx) => {
    // The backend relies on the entity ID field for table joins, so
    // it's not a valid/available custom field name.
    if (values.field_name === ENTITY_ID_FIELD_NAMES[entityType]) {
      ctx.addIssue({
        code: "custom",
        message: `"${values.field_name}" is a reserved identifier for ${customFieldEntityTypePlural(entityType)}.`,
        path: ["field_name"],
      });
    }

    if (values.field_type !== CustomFieldType.Enum) {
      return;
    }

    // Blank rows are ignored rather than rejected, and dropped on submit, so an empty row
    // left behind in the editor never blocks submission.
    const seen = new Set<string>();
    let attempted = 0;

    values.enum_values.forEach((option, index) => {
      const forbidden = forbiddenCharacterIn(option.value);
      if (forbidden !== undefined) {
        ctx.addIssue({
          code: "custom",
          message: `Option cannot contain ${forbidden}.`,
          path: ["enum_values", index, "value"],
        });

        attempted += 1;
        return;
      }

      const normalized = normalizeEnumValue(option.value);
      if (normalized.length === 0) {
        return;
      }

      attempted += 1;

      // Counted on what was typed, less surrounding whitespace. The server counts the value
      // it receives, which is this one with its whitespace runs collapsed -- never longer, so
      // an option accepted here cannot be refused there.
      if (option.value.trim().length > MAX_ENUM_VALUE_LENGTH) {
        ctx.addIssue({
          code: "custom",
          message: `Option must be ${MAX_ENUM_VALUE_LENGTH} characters or fewer.`,
          path: ["enum_values", index, "value"],
        });
      }

      if (seen.has(normalized)) {
        ctx.addIssue({
          code: "custom",
          message: "Duplicate option.",
          path: ["enum_values", index, "value"],
        });
      }

      seen.add(normalized);
    });

    // A row rejected for a forbidden character has still been filled in, so it counts here:
    // being told to add an option on top of the character error would send the reader looking
    // for the wrong problem.
    if (attempted === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Add at least one option.",
        path: ["enum_values", 0, "value"],
      });
    }

    if (seen.size > MAX_ENUM_OPTIONS) {
      ctx.addIssue({
        code: "custom",
        message: `A field can have at most ${MAX_ENUM_OPTIONS} options.`,
        path: ["enum_values", 0, "value"],
      });
    }
  });
}

/**
 * Maps validated form values to the create request: trims and drops empty optional text
 * fields, and assembles enum options (non-empty, whitespace-tidied) only for enum fields.
 *
 * Options go out in the form the user saw, not the canonical form. Composing them to NFC here
 * would send a value the server then measures against a limit the form already cleared.
 */
export function buildCreateCustomFieldRequest(
  values: CreateCustomFieldFormValues,
  entityType: TargetEntityType,
): CreateCustomFieldRequest {
  const displayName = values.display_name.trim();
  const description = values.description.trim();

  const request: CreateCustomFieldRequest = {
    entity_type: entityType,
    field_name: values.field_name,
    field_type: values.field_type,
  };

  if (displayName.length > 0) {
    request.display_name = displayName;
  }
  if (description.length > 0) {
    request.description = description;
  }
  if (values.field_type === CustomFieldType.Enum) {
    request.options = {
      field_type: CustomFieldType.Enum,
      enum_values: values.enum_values
        .map((option) => collapseEnumValueWhitespace(option.value))
        .filter((value) => value.length > 0),
    };
  }

  return request;
}
